import { toISODate } from "@rumbledirect/ui-v2/day";

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Number of milliseconds in a week, used to count whole ISO weeks between two
 * Monday anchors.
 */
const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;

/**
 * Narrows a value from the URL's search params to a `YYYY-MM-DD` date, so a
 * hand-edited link falls back to a sane default instead of sending the backend
 * something it will reject.
 */
export function toPeriodDate(value: unknown): string | undefined {
  return typeof value === "string" && parsePeriodDate(value)
    ? value
    : undefined;
}

/**
 * Parses a `YYYY-MM-DD` date at local midday, or `undefined` when the value is
 * not one.
 *
 * Midday rather than midnight, because a daylight-saving shift moves midnight
 * across the date boundary and would silently return the neighbouring day.
 *
 * `new Date` also rolls an impossible day over (31 February becomes 3 March),
 * so the round-trip is what actually rejects it: silently shifting a bound to a
 * date nobody wrote would be worse than treating it as unparseable.
 */
export function parsePeriodDate(value: string): Date | undefined {
  if (!ISO_DATE_PATTERN.test(value)) return undefined;

  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return undefined;

  return toISODate(parsed) === value ? parsed : undefined;
}

/**
 * The Monday of the ISO week the date falls in, which is how the backend
 * widens the requested period before it aggregates.
 */
export function startOfIsoWeek(date: Date): Date {
  const result = new Date(date);
  // `getDay` counts from Sunday; ISO weeks start on Monday, so Sunday is the
  // seventh day rather than the first.
  const isoDayIndex = (result.getDay() + 6) % 7;
  result.setDate(result.getDate() - isoDayIndex);
  return result;
}

/**
 * How many whole ISO weeks the inclusive period spans once both bounds are
 * widened to their week, mirroring what the backend will do with them.
 *
 * Returns 0 for an inverted or unparseable period; a caller that cares about
 * ordering checks that separately.
 */
export function countIsoWeeks(from: string, to: string): number {
  const start = parsePeriodDate(from);
  const end = parsePeriodDate(to);
  if (!start || !end || start > end) return 0;

  const spanned =
    startOfIsoWeek(end).getTime() - startOfIsoWeek(start).getTime();

  // Both anchors sit at local midday, so a daylight-saving hour cannot push the
  // difference past a half-week and rounding recovers the exact week count.
  return Math.round(spanned / MS_PER_WEEK) + 1;
}
